import * as React from "react";
import { ArrowRight, ChevronRight, RotateCcw, Layers, X } from "../../icons";
import { Button } from "../../button";
import { cn } from "../../utils";
import { INTENT_CLASSES } from "../../tokens/intents";
import type { PlayActionDTO, PlayKind } from "../../api/contracts/play";
import { KIND_META } from "./shared";
import { DarkRoom, BrandMark } from "./dark-room";

/**
 * The session-aware front door to Play — the screen the takeover opens on.
 *
 * The shipped takeover dropped straight onto move 1; this puts a front door back,
 * session-aware (the original launcher's job, split by whether you've been here
 * this tab session — gated in `play-driver.tsx` via `sessionStorage`):
 *
 *  • COLD (first open this session) → the Marquee launcher: a confident "Start
 *    all", or jump to a lane (category, by kind + count). The option like the
 *    original.
 *  • WARM (returning this session) → Resume: "pick up where you left off", with
 *    quiet escapes to start from the top or pick a lane.
 *
 * Both hand control back to PlayDriver, which seeks the SetupDriver walk to the
 * chosen set (the same `PlayTakeoverShell` the walk always used). Painted in the
 * same dark room as the walk — `.dark` scoped so every atom resolves its dark
 * tokens natively (no per-element repaints). Reuses the shared `DarkRoom` + the
 * real product mark rather than forking them.
 */

interface Lane {
  kind: PlayKind;
  count: number;
  label: string;
  Icon: (typeof KIND_META)[PlayKind]["Icon"];
  intent: (typeof KIND_META)[PlayKind]["intent"];
}

/** The category lanes present in the queue — the choose-your-own-adventure menu. */
function lanesFor(actions: PlayActionDTO[]): Lane[] {
  const counts = new Map<PlayKind, number>();
  for (const a of actions) counts.set(a.kind, (counts.get(a.kind) ?? 0) + 1);
  return [...counts.entries()]
    .map(([kind, count]) => {
      const meta = KIND_META[kind];
      return { kind, count, label: meta.label, Icon: meta.Icon, intent: meta.intent };
    })
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export interface PlayEntranceShellProps {
  actions: PlayActionDTO[];
  /** Ranked total (≥ actions.length when capped) — the "Start all" count. */
  total: number;
  /** Which door to open on: cold = the launcher, warm = resume. */
  mode: "cold" | "warm";
  /** 1-based ranked position the returning user left off on (warm copy). */
  resumeIndex: number;
  /** Moves still queued from the resume point (warm copy). */
  remaining: number;
  onStartAll: () => void;
  onStartLane: (kind: PlayKind) => void;
  onContinue: () => void;
  onClose?: () => void;
}

export function PlayEntranceShell({
  actions,
  total,
  mode,
  resumeIndex,
  remaining,
  onStartAll,
  onStartLane,
  onContinue,
  onClose,
}: PlayEntranceShellProps) {
  // Within the door the user can flip resume ↔ launcher; the initial face is the
  // session mode (cold → launcher, warm → resume).
  const [face, setFace] = React.useState<"resume" | "marquee">(
    mode === "warm" ? "resume" : "marquee",
  );
  const rootRef = React.useRef<HTMLDivElement>(null);

  // Focus the primary affordance (Radix initially focuses the close button); runs
  // after Radix's open-autofocus, and re-runs when the face flips.
  React.useEffect(() => {
    rootRef.current
      ?.querySelector<HTMLElement>("[data-entrance-focus]")
      ?.focus({ preventScroll: true });
  }, [face]);

  return (
    <div
      ref={rootRef}
      className="dark relative flex h-full w-full items-center justify-center overflow-hidden text-foreground"
    >
      <DarkRoom />

      <button
        type="button"
        onClick={() => onClose?.()}
        aria-label="Close Play"
        className="absolute right-6 top-6 z-10 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
      >
        <X strokeWidth={1.5} className="h-4 w-4" />
      </button>

      <div className="relative z-[1] flex w-full max-w-[30rem] flex-col gap-7 px-8 duration-200 animate-in fade-in-0">
        <div className="flex items-center gap-2.5">
          <BrandMark size={20} className="text-foreground" />
          <span className="type-subtext font-medium uppercase tracking-[0.22em] text-muted-foreground/60">
            Play
          </span>
        </div>

        {face === "resume" ? (
          <Resume
            total={total}
            resumeIndex={resumeIndex}
            remaining={remaining}
            onContinue={onContinue}
            onStartOver={onStartAll}
            onBrowse={() => setFace("marquee")}
          />
        ) : (
          <Marquee
            total={total}
            lanes={lanesFor(actions)}
            returning={mode === "warm"}
            onStartAll={onStartAll}
            onStartLane={onStartLane}
            onBackToResume={() => setFace("resume")}
          />
        )}
      </div>
    </div>
  );
}

/* ── COLD: the Marquee launcher ────────────────────────────────────────────── */

function Marquee({
  total,
  lanes,
  returning,
  onStartAll,
  onStartLane,
  onBackToResume,
}: {
  total: number;
  lanes: Lane[];
  returning: boolean;
  onStartAll: () => void;
  onStartLane: (kind: PlayKind) => void;
  onBackToResume: () => void;
}) {
  return (
    <>
      <div className="flex flex-col gap-3">
        <h2 className="type-heading font-medium tracking-[-0.02em] text-foreground">
          What do you want to work on?
        </h2>
        <p className="type-body leading-body text-muted-foreground">
          {total} moves ranked and ready. Take them in order, or jump to a lane.
        </p>
      </div>

      <Button
        data-entrance-focus
        size="lg"
        className="w-full justify-center"
        onClick={onStartAll}
      >
        Start all · {total}
        <ArrowRight strokeWidth={1.5} className="h-4 w-4" />
      </Button>

      <div className="flex flex-col">
        <span className="mb-2 type-subtext font-medium uppercase tracking-[0.18em] text-muted-foreground/55">
          Or pick a lane
        </span>
        {lanes.map((l) => (
          <LaneRow key={l.kind} lane={l} onClick={() => onStartLane(l.kind)} />
        ))}
      </div>

      {returning ? (
        <button
          type="button"
          onClick={onBackToResume}
          className="self-start type-subtext text-muted-foreground/55 underline-offset-4 transition-colors hover:text-muted-foreground hover:underline"
        >
          ← Back to where you left off
        </button>
      ) : null}
    </>
  );
}

function LaneRow({ lane, onClick }: { lane: Lane; onClick: () => void }) {
  const Icon = lane.Icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group -mx-2 flex items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-white/5"
    >
      <span
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
          INTENT_CLASSES[lane.intent].tint,
        )}
      >
        <Icon strokeWidth={1.5} className="h-3.5 w-3.5" />
      </span>
      <span className="flex-1 type-body font-medium text-foreground">{lane.label}</span>
      <span className="type-subtext tabular-nums text-muted-foreground/55">{lane.count}</span>
      <ChevronRight
        strokeWidth={1.5}
        className="h-4 w-4 text-muted-foreground/55 opacity-0 transition-opacity group-hover:opacity-100"
      />
    </button>
  );
}

/* ── WARM: Resume ──────────────────────────────────────────────────────────── */

function Resume({
  total,
  resumeIndex,
  remaining,
  onContinue,
  onStartOver,
  onBrowse,
}: {
  total: number;
  resumeIndex: number;
  remaining: number;
  onContinue: () => void;
  onStartOver: () => void;
  onBrowse: () => void;
}) {
  return (
    <>
      <div className="flex flex-col gap-3">
        <h2 className="type-heading font-medium tracking-[-0.02em] text-foreground">
          Pick up where you left off.
        </h2>
        <p className="type-body leading-body text-muted-foreground">
          You stopped on move {resumeIndex} of {total} — {remaining}{" "}
          {remaining === 1 ? "move is" : "moves are"} still in your queue.
        </p>
      </div>

      <Button
        data-entrance-focus
        size="lg"
        className="w-full justify-center"
        onClick={onContinue}
      >
        Continue
        <ArrowRight strokeWidth={1.5} className="h-4 w-4" />
      </Button>

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onStartOver}
          className="inline-flex items-center gap-1.5 type-body text-muted-foreground transition-colors hover:text-foreground"
        >
          <RotateCcw strokeWidth={1.5} className="h-3.5 w-3.5" />
          Start from the top
        </button>
        <button
          type="button"
          onClick={onBrowse}
          className="inline-flex items-center gap-1.5 type-body text-muted-foreground transition-colors hover:text-foreground"
        >
          <Layers strokeWidth={1.5} className="h-3.5 w-3.5" />
          Pick a lane
        </button>
      </div>
    </>
  );
}
